import { useCallback, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useMixpanelEvent from "./useMixpanelEvent";
import useCustomLoginWithRedirect from "./useCustomLoginWithRedirect";
import {
    addReaction,
    removeReaction,
    getReactionCounts,
    getUserReactionsForProperty,
} from "@/services/ReactionService";
import { unicodeToEmoji, emojiToUnicode, REACTION_EMOJIS } from "@/constants/reaction-unicode";
import { User } from "@/types";

interface UseReactionsReturn {
    reactions: { [emoji: string]: number };
    userReactions: string[];
    handleReactionToggle: (reaction: string, source?: string) => void;
    isCountsLoading: boolean;
    isCountsError: boolean;
    isUserReactionsLoading: boolean;
    isUserReactionsError: boolean;
    addReactionMutation: any;
    removeReactionMutation: any;
}

interface UserReaction {
    reactableId: string;
    reactionType: string;
}

/**
 * useReactions hook for managing reactions using backend API
 * @param reactableType - e.g., 'Property'
 * @param reactableId - e.g., propertyId
 * @param userState - user context
 * @param pageName - The name of the page for analytics (e.g., 'Property Details', 'Property Feed - Trending')
 */
const useReactions = (
    reactableType: string,
    reactableId: string,
    userState: User | null,
    pageName: string = "Property Details",
): UseReactionsReturn => {
    const userId = userState?.id;
    const queryClient = useQueryClient();
    const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();
    const { customLoginWithRedirect } = useCustomLoginWithRedirect();

    // Fetch reaction counts for this reactable
    const {
        data: reactionCountsData,
        isLoading: isCountsLoading,
        isError: isCountsError,
    } = useQuery({
        queryKey: ["reactionCounts", reactableType, reactableId],
        queryFn: () => getReactionCounts(reactableType, reactableId),
        enabled: !!reactableType && !!reactableId,
    });

    // Fetch all user reactions for listings
    const {
        data: userReactionsData,
        isLoading: isUserReactionsLoading,
        isError: isUserReactionsError,
    } = useQuery({
        queryKey: ["userReactions", userId, reactableId],
        queryFn: () => {
            if (!userId) throw new Error("User ID is required");
            return getUserReactionsForProperty(userId, reactableId);
        },
        enabled: !!userId && !!reactableId,
    });

    // Add reaction mutation
    const addReactionMutation = useMutation({
        mutationFn: ({ reaction }: { reaction: string }) => {
            if (!userId) throw new Error("User ID is required");
            return addReaction({ userId, reactableType, reactableId, reaction });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["reactionCounts", reactableType, reactableId] });
            queryClient.invalidateQueries({ queryKey: ["userReactions", userId, reactableId] });
            queryClient.invalidateQueries({ queryKey: ["user-reactions", userId, reactableId] });
        },
    });

    // Remove reaction mutation
    const removeReactionMutation = useMutation({
        mutationFn: ({ reaction }: { reaction: string }) => {
            if (!userId) throw new Error("User ID is required");
            return removeReaction({ userId, reactableType, reactableId, reaction });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["reactionCounts", reactableType, reactableId] });
            queryClient.invalidateQueries({ queryKey: ["userReactions", userId, reactableId] });
            queryClient.invalidateQueries({ queryKey: ["user-reactions", userId, reactableId] });
        },
    });

    // Helper: get reactions object for this reactable (convert Unicode to emoji for display)
    const reactions = useMemo(() => {
        if (!reactionCountsData) {
            // Initialize with all available reactions from constants
            const initialReactions: { [emoji: string]: number } = {};
            REACTION_EMOJIS.forEach((emoji) => {
                initialReactions[emoji] = 0;
            });
            return initialReactions;
        }

        // Convert Unicode keys to emoji for display
        const convertedReactions: { [emoji: string]: number } = {};
        Object.entries(reactionCountsData).forEach(([unicode, count]) => {
            const emoji = unicodeToEmoji(unicode);
            convertedReactions[emoji] = count;
        });

        return convertedReactions;
    }, [reactionCountsData]);

    // Helper: get user reactions for this reactable (convert Unicode to emoji)
    const userReactions = useMemo(() => {
        if (userReactionsData && Array.isArray(userReactionsData)) {
            // userReactionsData.data is an array of { reactableId, reactionType (Unicode) }
            return userReactionsData
                .filter((r: UserReaction) => r.reactableId === reactableId)
                .map((r: UserReaction) => unicodeToEmoji(r.reactionType)); // Convert Unicode to emoji
        }
        return [];
    }, [userReactionsData, reactableId]);

    // Toggle reaction (add if not present, remove if present)
    const handleReactionToggle = useCallback(
        (reaction: string, source: string = "reaction_sheet") => {
            // Check if user is properly authenticated with a valid ID
            if (!userId || userId === "" || userId === "null") {
                // Redirect to signup instead of just logging a warning
                customLoginWithRedirect({
                    authorizationParams: {
                        redirect_uri: window.location.origin + "/social",
                        screen_hint: "signup",
                    },
                    appState: {
                        toggleProfile: true,
                        showSetNicknameModal: true,
                        event: {
                            name: "reaction_signup_click",
                            referral_code: localStorage.getItem("referral_code"),
                        },
                    },
                });
                return;
            }

            const alreadyReacted = userReactions.includes(reaction);
            const action = alreadyReacted ? "remove" : "add";
            const unicode = emojiToUnicode(reaction);

            if (alreadyReacted) {
                removeReactionMutation.mutate({ reaction });
            } else {
                // Only for adding reaction for now
                sendMixpanelEvent("Reaction", {
                    isAuthenticated: !!userState?.id,
                    user_id: userState?.id || null,
                    nickname: userState?.nickname || null,
                    email: userState?.emailAddress || null,
                    pageName,
                    action,
                    source,
                    reaction: `${reaction} - ${unicode}`,
                });
                addReactionMutation.mutate({ reaction });
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            userReactions,
            userId,
            pageName,
            sendMixpanelEvent,
            addReactionMutation,
            removeReactionMutation,
            customLoginWithRedirect,
            reactableType,
            reactableId,
            userState?.id,
            userState?.nickname,
            userState?.emailAddress,
        ],
    );

    return {
        reactions,
        userReactions,
        handleReactionToggle,
        isCountsLoading,
        isCountsError,
        isUserReactionsLoading,
        isUserReactionsError,
        addReactionMutation,
        removeReactionMutation,
    };
};

export default useReactions;
